import React, { useMemo } from "react";
import { Flex, Text, View } from "@adobe/react-spectrum";
import { IndividualTask } from "../../../amazon-connect/models/task";
import Emitter from "../../../emitter";
import storeRTK from "../../../store/storeRTK";
import { setSelectedTab } from "../../../actions/common";
import { getInitialsAndColor } from "../../../utils";
import HistoryCallButton from "../../common/HistoryCallButton/HistoryCallButton";
import { API_CALL_DIAL } from "../../../constants/events";
import { setCurrentSessionTab } from "../../../actions/session";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { SpeedDialData } from "../../../types/generic.types";

interface CaseInfoDetailProps {
  activeCase: IndividualTask;
}

const CaseInfoDetail: React.FC<CaseInfoDetailProps> = ({ activeCase }) => {
  let caseAttributes = activeCase?.customer?.attributes;
  let speedDialState = useSelector(
    (state: RootState) => state.history.speedDials
  );

  const { initials, avatarColor, displayName } = useMemo(() => {
    const phoneNumber = caseAttributes?.PHONE;
    const speedDialEntry = phoneNumber
      ? speedDialState.find(
          (entry: SpeedDialData) => entry.phoneNumber === phoneNumber
        )
      : null;

    const first = caseAttributes?.FIRSTNAME?.[0] || "";
    const last = caseAttributes?.LASTNAME?.[0] || "";
    const fallbackInitials = `${first}${last}`.trim();
    const fallbackName = caseAttributes?.FIRSTNAME
      ? `${caseAttributes?.FIRSTNAME} ${caseAttributes?.LASTNAME ?? ""}`
      : "NA";

    const initials = speedDialEntry
      ? `${speedDialEntry.firstName[0]}${speedDialEntry.lastName[0]}`
      : fallbackInitials;
    const displayName = speedDialEntry
      ? `${speedDialEntry.firstName} ${speedDialEntry.lastName}`
      : fallbackName;

    return {
      initials,
      avatarColor: getInitialsAndColor(initials, "color"),
      displayName,
    };
  }, [caseAttributes, speedDialState]);

  const dialCall = (phoneNumber: string | undefined) => {
    const formattedNumber = phoneNumber?.startsWith("+")
      ? phoneNumber
      : `+${phoneNumber}`;

    Emitter.emit(API_CALL_DIAL, { phoneNumber: formattedNumber });

    setTimeout(() => {
      storeRTK.dispatch(setSelectedTab("Call"));
      storeRTK.dispatch(setCurrentSessionTab({ id: "call" }));
    }, 200);
  };

  return (
    <View UNSAFE_className="caseInfoDetail-wrapper">
      <Flex direction={"row"}>
        <Flex direction={"column"} width={"91%"}>
          <Flex
            direction={"row"}
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex direction="row" alignItems="center" width="50%">
              <View UNSAFE_className="call-history-avatar">
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  UNSAFE_className="agent-avatar"
                  UNSAFE_style={{
                    backgroundColor: avatarColor as string,
                    borderRadius: "50%",
                  }}
                >
                  <Text UNSAFE_className="call-monitor-agent-text">
                    {initials}
                  </Text>
                </Flex>
              </View>
              <Flex direction="column" marginStart="12px">
                <Text margin="0" UNSAFE_className="case-info-subheading">
                  {displayName}
                </Text>
                <Text UNSAFE_className="case-detail-item-info">
                  {caseAttributes?.EMAIL || "-"}
                </Text>
              </Flex>
            </Flex>
            <View UNSAFE_className="case-detail-item case-id-block" width="50%">
              <Text UNSAFE_className="case-detail-item-heading">Org ID</Text>
              <Text UNSAFE_className="case-detail-item-info">
                {caseAttributes?.ORGID || "-"}
              </Text>
            </View>
          </Flex>

          <Flex
            UNSAFE_className="caseInfoDetail-wrapper-bottom"
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex UNSAFE_className="case-detail-item" width="25%">
              <Text UNSAFE_className="case-detail-item-heading">Region</Text>
              <Text UNSAFE_className="case-detail-item-info">
                {caseAttributes?.REGIONTEXT || "-"}
              </Text>
            </Flex>
            <Flex UNSAFE_className="case-detail-item" width="25%">
              <Text UNSAFE_className="case-detail-item-heading">Country</Text>
              <Text UNSAFE_className="case-detail-item-info">
                {caseAttributes?.COUNTRY || "-"}
              </Text>
            </Flex>
            <Flex UNSAFE_className="case-detail-item" width="25%">
              <Text UNSAFE_className="case-detail-item-heading">Org name</Text>
              <Text UNSAFE_className="case-detail-item-info">
                {caseAttributes?.ORGNAME || "-"}
              </Text>
            </Flex>
            <Flex
              UNSAFE_className="case-detail-item"
              width="25%"
              alignItems={"center"}
            >
              <Text UNSAFE_className="case-detail-item-heading">
                Phone number
              </Text>
              <Text UNSAFE_className="case-detail-item-info">
                {caseAttributes?.PHONE || "-"}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex width={"9%"}>
          <HistoryCallButton onClick={() => dialCall(caseAttributes?.PHONE)} />
        </Flex>
      </Flex>
    </View>
  );
};

export default CaseInfoDetail;
